import { Suspense, useEffect, memo } from "react"
import { Switch, Route, useLocation, BrowserRouter } from "react-router-dom"
import { createBrowserHistory } from "history"
import { ThemeProvider } from "styled-components"
import { RecoilRoot } from "recoil"
import { RecoilURLSyncJSON } from "recoil-sync"
import { QueryClient, QueryClientProvider } from "react-query"
import { ErrorBoundary, Provider, useRollbar } from "@rollbar/react"
import { loadStripe } from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"
import FacebookPixel from "react-facebook-pixel"
import ReactGA from "react-ga"
import { useDecision, setLogger } from "@optimizely/react-sdk"
import { useTranslation } from "react-i18next"
import { LoadScript } from "@react-google-maps/api"
import { Container, GlobalStyle, AppContainer, NavRow, MobileNav, NavCol } from "./App.styles"
import Dashboard from "./dashboard"
import Settings from "./settings"
import Organize from "./organize"
import Teams from "./teams"
import Monitor from "./monitor"
import AnswerShow from "./monitor/AnswerShow"
import GameStream from "./monitor/GameStream"
import Leaderboard from "./monitor/Leaderboard"
import ChallengeStream from "./monitor/ChallengeStream"
import TeamStream from "./monitor/TeamStream"
import Rewards from "./rewards"
import Exports from "./exports"
import Branding from "./branding"
import Slideshow from "./slideshow"
import Personalizations from "./personalizations"
import Library from "./library"
import Reels from "./reels"
import SignIn from "./sign-in"
import SignUp from "./sign-up"
import ResetPassword from "./reset-password"
import Donation from "./donation"
import Analytics from "./analytics"
import Path from "./path"
import NpcPage from "./npc"
import DesignationPage from "./designations"
import {
    Error,
    ErrorDisplay,
    SideNav,
    Header,
    Footer,
    Maintenance,
    ServerError,
} from "./common"
import theme from "./themes"
import PrivateRoute from "./common/PrivateRoute"
import ScrollToTop from "./common/ScrollToTop"
import PrivacyPolicy from "./common/PrivacyPolicy"
import TermsAndCondition from "./common/TermsAndCondition"
import Groups from "./groups"
import useViewport from "./hooks/useViewport"
import "./lib/timeago"
import "./lib/i18n"
import "./style.scss"
import { LoadingDisplayIndicator } from "./common/LoadingDisplay"
import { getCurrentLanguage } from "./lib/lang"
import AppAuthProvider from './AppAuthProvider'
import RequestIndicator from "./common/RequestIndicator"
import AddDockTooltip from './common/AddDockTooltip'
import ROUTE_PATTERNS from "./consts/routes"

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: (count, resp) => {
                const { response } = resp || {}
                const { status } = response || {}
                return status !== 401 && status !== 403 && count < 3
            },
            retryDelay: attempt =>
                Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
        },
    },
})

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

const rollbarConfig = {
    accessToken: "52b44c7650074dac83310b2851b43f7a",
    environment: process.env.NODE_ENV,
    checkIgnore: (_, __, payload) => {
        // Get the error message
    const message = payload?.body?.trace?.exception?.message ||
        payload?.body?.message?.body || '';

    // Example: ignore specific messages
    if (message.includes('Request failed with status code 401')) {
        return true; // Ignore this error
    }

    return false; // Send all others to Rollbar
    }
}

const history = createBrowserHistory()

history.listen((location) => {
    ReactGA.set({
        page: `${location.pathname}${location.search}`
    })
    ReactGA.send("pageview")
    FacebookPixel.pageView()
})

setLogger(null)

const RollbackContainer = ({ children }) => {
    const location = useLocation()
    const rollbar = useRollbar()
    if (process.env.REACT_APP_OVERRIDE_CONSOLE === "true") {
        console.error = err => {
            const ignoreList = [
                'no-op',
                'status code',
                'OPTIMIZELY',
            ]
            ignoreList.find(key => key.match(err))
                ? console.error(err)
                : rollbar.error(err)
        }
        console.debug = debug => rollbar.debug(debug)
    }

    return (
        <ErrorBoundary
            fallbackUI={(error, resetError) => {
                const errorObject = error?.error || error
                return (
                    <ErrorDisplay error={errorObject} resetError={resetError} />
                )
            }}
            key={location.pathname}
        >
            {children}
        </ErrorBoundary>
    )
}

const LanguageContainer = ({ children }) => {
    const [decisionLang] = useDecision('language')
    const { i18n } = useTranslation()

    useEffect(() => {
        if (!decisionLang.enabled) {
            i18n.changeLanguage('disabled')
        }
    }, [decisionLang.enabled, i18n])
    return children
}

const RouterWrapper = () => {
    return (
        <Elements stripe={stripePromise}>
            <ScrollToTop />
            <Switch>
                <PrivateRoute
                    exact
                    path={ROUTE_PATTERNS.ROOT}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Dashboard}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.DASHBOARD}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Dashboard}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAME_EDIT}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Settings}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.TASKS_ORGANIZE}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Organize}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_PATH}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Path}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.TEAMS_ORGANIZE}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Teams}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GROUPS_ORGANIZE}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Groups}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_REWARDS}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Rewards}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_EXPORTS}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Exports}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.ANSWERS_MONITOR}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Monitor}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_GREY}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Branding}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_REELS}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Reels}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.TASKS_LIBRARY}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Library}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_STREAM}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={GameStream}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.TASKS_STREAM}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={ChallengeStream}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.TEAMS_STREAM}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={TeamStream}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.TEAMS_LEADERBOARD}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Leaderboard}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.ANSWERS_SHOW}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={AnswerShow}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_SLIDESHOW}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Slideshow}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_PERSONALIZATIONS}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Personalizations}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_ANALYTICS}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Analytics}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_DONATE}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={Donation}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_NPC}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={NpcPage}
                />
                <PrivateRoute
                    path={ROUTE_PATTERNS.GAMES_DESIGNATIONS}
                    loginPath={ROUTE_PATTERNS.SIGN_IN}
                    component={DesignationPage}
                />
                <Route path={ROUTE_PATTERNS.SIGN_IN}>
                    <SignIn />
                </Route>
                <Route path={ROUTE_PATTERNS.SIGN_UP}>
                    <SignUp />
                </Route>
                <Route path={ROUTE_PATTERNS.RESET_PASSWORD}>
                    <ResetPassword />
                </Route>
                <Route path={ROUTE_PATTERNS.MAINTENANCE}>
                    <Maintenance />
                </Route>
                <Route path={ROUTE_PATTERNS.SERVER_ERROR}>
                    <ServerError />
                </Route>
                <Route path={ROUTE_PATTERNS.PRIVACY}>
                    <PrivacyPolicy />
                </Route>
                <Route path={ROUTE_PATTERNS.TERMS}>
                    <TermsAndCondition />
                </Route>
                <Route path="*">
                    <Error
                        title="Page Not Found!"
                        message="We're unable to find the page you're looking for."
                    />
                </Route>
            </Switch>
        </Elements>
    )
}

const ResponsiveNav = memo(({ nav, children }) => {
    const { width } = useViewport()
    const breakpoint = 600

    return width < breakpoint
        ? (
            <MobileNav>
                {nav}
                {children}
            </MobileNav>
        )
        : (
            <>
                <NavRow>
                    <NavCol className="col--stretch nav-wrapper">{nav}</NavCol>
                    <NavCol className="col--stretch">
                        <div id="app-container-children"></div>
                        {children}
                    </NavCol>
                </NavRow>
            </>
        )
})

const MapLoader = () => null
const App = () => {
    FacebookPixel.init(process.env.REACT_APP_FACEBOOK_PIXEL_ID)
    ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_ID)
    const language = getCurrentLanguage()
    return (
        <Provider config={rollbarConfig}>
            <BrowserRouter history={history}>
                <ThemeProvider theme={theme}>
                    <RecoilRoot>
                        <RecoilURLSyncJSON
                            location={{
                                part: "queryParams",
                            }}
                        >
                            <AppAuthProvider>
                                <RollbackContainer>
                                    <QueryClientProvider client={queryClient}>
                                        <Suspense fallback={<LoadingDisplayIndicator />}>
                                            <LanguageContainer>
                                                <LoadScript
                                                    id="google-map-script"
                                                    googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                                                    language={language}
                                                    loadingElement={<MapLoader />}
                                                    libraries={['drawing', 'places']}
                                                >
                                                    <GlobalStyle />
                                                    <Header />
                                                    <AddDockTooltip />
                                                    <RequestIndicator />
                                                    <AppContainer className="d-flex" id="app-container">
                                                        <ResponsiveNav nav={<SideNav />}>
                                                            <Container>
                                                                <RouterWrapper />
                                                                <Footer />
                                                            </Container>
                                                        </ResponsiveNav>
                                                    </AppContainer>
                                                </LoadScript>
                                            </LanguageContainer>
                                        </Suspense>
                                    </QueryClientProvider>
                                </RollbackContainer>
                            </AppAuthProvider>
                        </RecoilURLSyncJSON>
                    </RecoilRoot>
                </ThemeProvider>
            </BrowserRouter>
        </Provider>
    )
}

export default App;